/// <reference types="@types/google.maps" />
import { useState, useEffect, useRef, useCallback } from 'react';
import { TextInput, Loader } from '@mantine/core';
import { IconMapPin, IconSearch, IconX } from '@tabler/icons-react';

interface PharmacySearchProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

interface PlacePrediction {
  description: string;
  place_id: string;
  mainText: string;
  secondaryText: string;
}

interface SelectedPlace {
  name: string;
  address: string;
  placeId: string;
  lat: number;
  lng: number;
}

// Google Places API key
const GOOGLE_PLACES_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY || '';

// Track if script is already loading/loaded globally
let scriptLoadPromise: Promise<boolean> | null = null;

// Default location (US center) as fallback
const DEFAULT_LOCATION = { lat: 39.8283, lng: -98.5795 };

export function PharmacySearch({
  value,
  onChange,
  disabled = false,
}: PharmacySearchProps) {
  const [inputValue, setInputValue] = useState(value);
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isApiLoaded, setIsApiLoaded] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<SelectedPlace | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number }>(DEFAULT_LOCATION);
  const containerRef = useRef<HTMLDivElement>(null);
  const justSelectedRef = useRef<string | null>(null);

  // Map refs
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const selectedMarkerRef = useRef<google.maps.Marker | null>(null);

  // Sync inputValue when value prop changes from parent (e.g., on data load)
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Get user's approximate location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => {
          // User denied or error - keep default location
          console.log('Geolocation not available, using default location');
        },
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 600000 }
      );
    }
  }, []);

  // Load Google Places API script
  useEffect(() => {
    if (!GOOGLE_PLACES_API_KEY) {
      console.warn('Google Places API key not configured. Using simple text input.');
      return;
    }

    // Check if already loaded
    if (window.google?.maps?.places) {
      setIsApiLoaded(true);
      return;
    }

    // Load the script only once
    if (!scriptLoadPromise) {
      scriptLoadPromise = new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places&loading=async`;
        script.async = true;
        script.onload = () => {
          setTimeout(() => {
            if (window.google?.maps?.places) {
              resolve(true);
            } else {
              resolve(false);
            }
          }, 100);
        };
        script.onerror = () => {
          console.error('Failed to load Google Places API script');
          resolve(false);
        };
        document.head.appendChild(script);
      });
    }

    scriptLoadPromise.then((success) => {
      if (success) {
        setIsApiLoaded(true);
      } else {
        setApiError('Failed to load Places API');
      }
    });
  }, []);

  // Track the location the map was initialized with
  const initializedLocationRef = useRef<{ lat: number; lng: number } | null>(null);

  // Filter to only include actual pharmacies (not vet clinics, hospitals, etc.)
  const isValidPharmacy = (place: google.maps.places.PlaceResult): boolean => {
    const name = (place.name || '').toLowerCase();
    const excludeTerms = ['veterinary', 'animal', 'pet', 'bird', 'vet ', 'hospital', 'medical center', 'clinic'];
    const includeTerms = ['pharmacy', 'cvs', 'walgreens', 'rite aid', 'h-e-b', 'heb', 'walmart', 'costco', 'target', 'kroger', 'safeway'];

    // Exclude if name contains exclusion terms
    if (excludeTerms.some(term => name.includes(term))) {
      return false;
    }

    // Include if name contains pharmacy-related terms
    if (includeTerms.some(term => name.includes(term))) {
      return true;
    }

    // Check types array for pharmacy
    const types = place.types || [];
    return types.includes('pharmacy');
  };

  // Load nearby pharmacies and create markers
  const loadNearbyPharmacies = useCallback((map: google.maps.Map, center: { lat: number; lng: number }) => {
    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    const service = new google.maps.places.PlacesService(map);
    const addedPlaceIds = new Set<string>();

    // Helper to add markers without duplicates
    const addMarkers = (results: google.maps.places.PlaceResult[] | null) => {
      if (!results) return;
      results.forEach((place) => {
        if (place.geometry?.location && place.place_id && !addedPlaceIds.has(place.place_id)) {
          if (isValidPharmacy(place)) {
            addedPlaceIds.add(place.place_id);
            createPharmacyMarker(map, place);
          }
        }
      });
    };

    // Do a text search for pharmacies (includes chains like CVS, Walgreens)
    service.textSearch(
      {
        query: 'pharmacy',
        location: center,
        radius: 10000,
      },
      (results, status) => {
        console.log('Pharmacy textSearch status:', status, 'results:', results?.length);
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          addMarkers(results);
        }
      }
    );
  }, []);

  // Initialize map when API is loaded
  useEffect(() => {
    if (!isApiLoaded || !mapRef.current || mapInstanceRef.current) return;

    const map = new google.maps.Map(mapRef.current, {
      center: userLocation,
      zoom: 13,
      styles: [
        { elementType: 'geometry', stylers: [{ color: '#1a1a2e' }] },
        { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1a2e' }] },
        { elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
        { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2d2d44' }] },
        { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e0e1a' }] },
        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
      ],
      disableDefaultUI: true,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });
    mapInstanceRef.current = map;
    initializedLocationRef.current = userLocation;

    // Load nearby pharmacies
    loadNearbyPharmacies(map, userLocation);
  }, [isApiLoaded, userLocation, loadNearbyPharmacies]);

  // Update map when user location changes (after geolocation completes)
  useEffect(() => {
    // Skip if map isn't ready or location hasn't changed
    if (!mapInstanceRef.current || !initializedLocationRef.current) return;

    // Skip if userLocation is same as what we initialized with
    if (
      userLocation.lat === initializedLocationRef.current.lat &&
      userLocation.lng === initializedLocationRef.current.lng
    ) {
      return;
    }

    // Pan to new user location and reload pharmacies
    initializedLocationRef.current = userLocation;
    mapInstanceRef.current.panTo(userLocation);
    loadNearbyPharmacies(mapInstanceRef.current, userLocation);
  }, [userLocation, loadNearbyPharmacies]);

  // Create a marker for a pharmacy
  const createPharmacyMarker = (map: google.maps.Map, place: google.maps.places.PlaceResult) => {
    if (!place.geometry?.location) return;

    console.log('Creating marker for:', place.name, 'at', place.geometry.location.lat(), place.geometry.location.lng());

    // Create marker with SVG pharmacy icon
    const marker = new google.maps.Marker({
      map,
      position: place.geometry.location,
      title: place.name,
      icon: {
        url: 'data:image/svg+xml,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="11" fill="#00d6f5" stroke="#ffffff" stroke-width="1.5"/>
            <text x="12" y="16" text-anchor="middle" fill="white" font-size="10" font-weight="bold" font-family="serif">Rx</text>
          </svg>
        `),
        scaledSize: new google.maps.Size(24, 24),
        anchor: new google.maps.Point(12, 12),
      },
    });

    marker.addListener('click', () => {
      console.log('Clicked place data:', { name: place.name, vicinity: place.vicinity, formatted_address: place.formatted_address, place });
      const name = place.name || '';
      const address = place.vicinity || place.formatted_address || '';
      const fullDescription = address ? `${name}, ${address}` : name;

      // Update form
      justSelectedRef.current = fullDescription;
      setInputValue(fullDescription);
      onChange(fullDescription);

      // Update selected place
      setSelectedPlace({
        name,
        address,
        placeId: place.place_id || '',
        lat: place.geometry?.location?.lat() || 0,
        lng: place.geometry?.location?.lng() || 0,
      });

      // Highlight selected marker
      if (selectedMarkerRef.current) {
        selectedMarkerRef.current.setIcon({
          url: 'data:image/svg+xml,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="11" fill="#00d6f5" stroke="#ffffff" stroke-width="1.5"/>
              <text x="12" y="16" text-anchor="middle" fill="white" font-size="10" font-weight="bold" font-family="serif">Rx</text>
            </svg>
          `),
          scaledSize: new google.maps.Size(24, 24),
          anchor: new google.maps.Point(12, 12),
        });
      }
      marker.setIcon({
        url: 'data:image/svg+xml,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
            <circle cx="14" cy="14" r="13" fill="#22c55e" stroke="#ffffff" stroke-width="2"/>
            <text x="14" y="18" text-anchor="middle" fill="white" font-size="11" font-weight="bold" font-family="serif">Rx</text>
          </svg>
        `),
        scaledSize: new google.maps.Size(28, 28),
        anchor: new google.maps.Point(14, 14),
      });
      selectedMarkerRef.current = marker;

      // Pan to selected location
      map.panTo(place.geometry!.location!);
    });

    markersRef.current.push(marker);
  };

  // Search for pharmacies using the new Places API
  const searchPharmacies = useCallback(
    async (query: string) => {
      if (!query || query.length < 2) {
        setPredictions([]);
        setIsLoading(false);
        return;
      }

      if (!isApiLoaded || apiError) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        // Use the new AutocompleteSuggestion API
        const searchQuery = query.toLowerCase().includes('pharmacy')
          ? query
          : `${query} pharmacy`;

        const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: searchQuery,
        });

        setIsLoading(false);

        if (suggestions && suggestions.length > 0) {
          const results: PlacePrediction[] = suggestions
            .slice(0, 5)
            .map((suggestion) => {
              const placePrediction = suggestion.placePrediction;
              return {
                description: placePrediction?.text?.text || '',
                place_id: placePrediction?.placeId || '',
                mainText: placePrediction?.mainText?.text || '',
                secondaryText: placePrediction?.secondaryText?.text || '',
              };
            })
            .filter((p) => p.description && p.place_id);

          setPredictions(results);
          setShowDropdown(true);
        } else {
          setPredictions([]);
        }
      } catch (err) {
        console.error('Pharmacy search error:', err);
        setIsLoading(false);

        if (err instanceof Error && err.message.includes('REQUEST_DENIED')) {
          setApiError('Places API not enabled');
        }
        setPredictions([]);
      }
    },
    [isApiLoaded, apiError]
  );

  // Debounced search
  useEffect(() => {
    if (justSelectedRef.current && inputValue === justSelectedRef.current) {
      return;
    }
    if (justSelectedRef.current && inputValue !== justSelectedRef.current) {
      justSelectedRef.current = null;
    }

    const timer = setTimeout(() => {
      if (inputValue) {
        searchPharmacies(inputValue);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, searchPharmacies]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    if (!newValue) {
      setPredictions([]);
      setSelectedPlace(null);
      // Reset selected marker
      if (selectedMarkerRef.current) {
        selectedMarkerRef.current.setIcon({
          url: 'data:image/svg+xml,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="11" fill="#00d6f5" stroke="#ffffff" stroke-width="1.5"/>
              <text x="12" y="16" text-anchor="middle" fill="white" font-size="10" font-weight="bold" font-family="serif">Rx</text>
            </svg>
          `),
          scaledSize: new google.maps.Size(24, 24),
          anchor: new google.maps.Point(12, 12),
        });
        selectedMarkerRef.current = null;
      }
    }
  };

  // Fetch place details to get coordinates
  const fetchPlaceDetails = useCallback(async (placeId: string, name: string, address: string) => {
    if (!isApiLoaded) return;

    setIsLoadingDetails(true);
    try {
      const place = new google.maps.places.Place({ id: placeId });
      await place.fetchFields({ fields: ['location'] });

      if (place.location) {
        const newPlace = {
          name,
          address,
          placeId,
          lat: place.location.lat(),
          lng: place.location.lng(),
        };
        setSelectedPlace(newPlace);

        // Pan map to selected location
        if (mapInstanceRef.current) {
          mapInstanceRef.current.panTo(place.location);
          mapInstanceRef.current.setZoom(15);
        }
      }
    } catch (err) {
      console.error('Failed to fetch place details:', err);
      setSelectedPlace({
        name,
        address,
        placeId,
        lat: 0,
        lng: 0,
      });
    } finally {
      setIsLoadingDetails(false);
    }
  }, [isApiLoaded]);

  // Handle selection from dropdown
  const handleSelect = (prediction: PlacePrediction) => {
    justSelectedRef.current = prediction.description;
    setInputValue(prediction.description);
    onChange(prediction.description);
    setPredictions([]);
    setShowDropdown(false);

    const address = prediction.secondaryText ||
      prediction.description.replace(prediction.mainText, '').replace(/^,\s*/, '');

    fetchPlaceDetails(prediction.place_id, prediction.mainText, address);
  };

  // Clear selection
  const handleClear = () => {
    setInputValue('');
    onChange('');
    setSelectedPlace(null);
    setPredictions([]);

    // Reset selected marker
    if (selectedMarkerRef.current) {
      selectedMarkerRef.current.setIcon({
        url: 'data:image/svg+xml,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="11" fill="#00d6f5" stroke="#ffffff" stroke-width="1.5"/>
            <text x="12" y="16" text-anchor="middle" fill="white" font-size="10" font-weight="bold" font-family="serif">Rx</text>
          </svg>
        `),
        scaledSize: new google.maps.Size(24, 24),
        anchor: new google.maps.Point(12, 12),
      });
      selectedMarkerRef.current = null;
    }

    // Reset map view
    if (mapInstanceRef.current) {
      mapInstanceRef.current.panTo(userLocation);
      mapInstanceRef.current.setZoom(13);
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle blur
  const handleBlur = () => {
    setTimeout(() => {
      setShowDropdown(false);
    }, 200);
  };

  const showPredictions = showDropdown && predictions.length > 0;
  const showSimpleInput = !GOOGLE_PLACES_API_KEY || apiError;

  return (
    <div ref={containerRef} className="relative">
      {/* Stacked layout: search on top, map below */}
      <div className="flex flex-col gap-4">
        {/* Search input section */}
        <div className="relative">
          <TextInput
            label="Preferred Pharmacy"
            placeholder={
              showSimpleInput
                ? 'Enter pharmacy name and address'
                : 'Search for a pharmacy (e.g., CVS on Congress)'
            }
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => predictions.length > 0 && setShowDropdown(true)}
            onBlur={handleBlur}
            disabled={disabled}
            leftSection={<IconSearch size={16} className="text-gray-500" />}
            rightSection={isLoading && !apiError ? <Loader size={16} /> : null}
            classNames={{
              input: 'input',
              label: 'text-sm font-medium text-gray-300 mb-1',
            }}
          />

          {showSimpleInput && (
            <p className="text-xs text-gray-500 mt-1">
              Tip: Enter the full pharmacy name and address
            </p>
          )}

          {/* Predictions dropdown */}
          {showPredictions && (
            <div className="absolute z-50 w-full mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl overflow-hidden max-h-[200px] overflow-y-auto">
              {predictions.map((prediction) => {
                const address = prediction.secondaryText ||
                  prediction.description.replace(prediction.mainText, '').replace(/^,\s*/, '');
                return (
                  <button
                    key={prediction.place_id}
                    type="button"
                    onClick={() => handleSelect(prediction)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-800 transition-colors flex items-start gap-3"
                  >
                    <IconMapPin size={18} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-200 font-medium">{prediction.mainText}</span>
                      {address && <span className="text-xs text-gray-400">{address}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Selected pharmacy info */}
          {selectedPlace && (
            <div className="mt-3 p-3 bg-gray-800/50 rounded-lg flex items-start justify-between">
              <div className="flex items-start gap-3">
                <IconMapPin size={18} className="text-green-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-200">{selectedPlace.name}</p>
                  <p className="text-xs text-gray-400">{selectedPlace.address}</p>
                </div>
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-gray-200 transition-colors"
                  title="Clear selection"
                >
                  <IconX size={16} />
                </button>
              )}
            </div>
          )}

          {/* Loading indicator */}
          {isLoadingDetails && (
            <div className="mt-3 p-3 bg-gray-800/50 rounded-lg flex items-center">
              <Loader size="sm" color="cyan" />
              <span className="ml-2 text-sm text-gray-400">Loading pharmacy details...</span>
            </div>
          )}
        </div>

        {/* Map section */}
        {GOOGLE_PLACES_API_KEY && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Nearby Pharmacies
              <span className="text-xs text-gray-500 font-normal ml-2">(click to select)</span>
            </label>
            <div
              ref={mapRef}
              className="rounded-lg overflow-hidden border border-gray-700 h-[280px] w-full"
            />
          </div>
        )}
      </div>
    </div>
  );
}
